<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';

const { content, contentAttributes } = useMessageContext();

const interactiveConfig = computed(
  () =>
    contentAttributes.value?.conversationFlowInteractive ||
    contentAttributes.value?.conversation_flow_interactive ||
    {}
);

const itemList = computed(
  () => contentAttributes.value?.items || contentAttributes.value?.Items || []
);

const quickReplyButton = button =>
  button?.quickReply || button?.quick_reply || button?.reply || {};

const carouselCards = computed(() => {
  const configuredCards = interactiveConfig.value?.cards || [];

  if (configuredCards.length) {
    return configuredCards.map(card => {
      const mediaType = card?.header?.type || 'image';
      const media = card?.header?.[mediaType] || {};
      const action = card?.action || {};

      const actions = ['button', 'reply'].includes(card?.type)
        ? (action.buttons || []).map(button => ({
            text: quickReplyButton(button)?.title || '',
            type: 'reply',
          }))
        : [
            {
              text: action?.parameters?.display_text || '',
              type: 'url',
              url: action?.parameters?.url || '',
            },
          ];

      return {
        body: card?.body?.text || '',
        mediaType,
        mediaUrl: media?.link || '',
        actions: actions.filter(item => item.text),
      };
    });
  }

  return itemList.value.map(item => ({
    body: item.description || item.title || '',
    mediaType: 'image',
    mediaUrl: item.mediaUrl || item.media_url || '',
    actions: (item.actions || []).filter(action => action?.text),
  }));
});
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="cards">
    <div class="flex flex-col gap-3">
      <p v-if="content" class="m-0 whitespace-pre-wrap text-n-slate-12">
        {{ content }}
      </p>

      <div class="flex gap-3 overflow-x-auto pb-1">
        <div
          v-for="(card, index) in carouselCards"
          :key="index"
          class="w-64 shrink-0 overflow-hidden rounded-lg border border-n-weak bg-n-slate-2"
        >
          <img
            v-if="card.mediaUrl && card.mediaType !== 'video'"
            :src="card.mediaUrl"
            class="h-36 w-full object-cover"
          />
          <video
            v-else-if="card.mediaUrl"
            :src="card.mediaUrl"
            controls
            class="h-36 w-full object-cover"
          />

          <div class="flex flex-col gap-3 p-3">
            <p
              v-if="card.body"
              class="m-0 whitespace-pre-wrap text-sm leading-6 text-n-slate-12"
            >
              {{ card.body }}
            </p>

            <div class="flex flex-col gap-2">
              <template
                v-for="(action, actionIndex) in card.actions"
                :key="actionIndex"
              >
                <a
                  v-if="action.type === 'url' && action.url"
                  :href="action.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex h-9 items-center justify-center rounded-lg bg-n-brand px-3 text-sm font-medium text-white no-underline"
                >
                  {{ action.text }}
                </a>
                <div
                  v-else
                  class="inline-flex h-9 items-center justify-center rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm font-medium text-n-slate-12"
                >
                  {{ action.text }}
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseBubble>
</template>
